import { Box, Grid, TextField } from "@mui/material";
import * as React from "react";
import { IdTitle, IIdTitle } from "ui/Scripts/source/models/common/idTitle";
import { Translations } from "ui/Scripts/source/models/translations";
import { ConfirmationDialogResult } from "ui/Scripts/source/models/enums";
import { AppUtils } from "../../../../../../../framework/appUtils";
import { Dialog } from "../../../../../../framework/Dialog/Dialog";
import { DialogButtons } from "../../../../../../framework/Dialog/DialogButtons";
import { SaveDataObj } from "source/framework/saveData";
import { GridRow } from "source/components/framework/Grid/GridRow";
import { FormField } from "ui/Scripts/source/components/framework/FormField";
import { DialogItemsNav } from "source/components/framework/Dialog/DialogItemsNav";

export interface IFormDesignerOptionDialogProp {
    isRadio: boolean;
    usedOptionTitles: string[];
    idOption: IIdTitle;
    isNew: boolean;
    hasPrevious: boolean;
    hasNext: boolean;
    onPrevious: () => void;
    onNext: () => void;
    onSaved: (idOption: IIdTitle) => void;
    onClose: () => void;
    onRemove: (title: string) => void;
}

export interface IFormDesignerOptionDialogState {
    title: string;
    id: string;
}

export class FormDesignerOptionDialog extends React.Component<IFormDesignerOptionDialogProp, IFormDesignerOptionDialogState> {
    private orgStateHash: string = "";

    getSaveDataFromState = (state: IFormDesignerOptionDialogState): SaveDataObj => {
        const result = new SaveDataObj();
        result.append("id", state.id);
        result.append("title", state.title);
        return result;
    };

    constructor(props: IFormDesignerOptionDialogProp) {
        super(props);
        this.state = {
            title: props.idOption.title,
            id: props.idOption.id
        };
        this.orgStateHash = this.getSaveDataFromState(this.state).hash;
    }

    async componentDidUpdate(prevProps: IFormDesignerOptionDialogProp, prevState: IFormDesignerOptionDialogState): Promise<void> {
        const props = this.props;
        const state = this.state;
        const prevTitle = prevProps.idOption.title ?? "";
        const title = props.idOption.title ?? "";
        if (prevTitle === title) return;
        const cancelResult = await AppUtils.cancel(this.getSaveDataFromState(state).hash, this.orgStateHash, null);
        if (cancelResult === ConfirmationDialogResult.Cancel) return;
        if (cancelResult === ConfirmationDialogResult.Yes) {
            await this.saveToDb();
        }
        this.setState({
            title: props.idOption.title,
            id: props.idOption.id
        }, () => { this.orgStateHash = this.getSaveDataFromState(this.state).hash; });
    }

    handleChange = (event) => {
        const target = event.target;
        const value: string = target.value;
        const name: string = target.name;
        if (name === "title") {
            this.setState({ title: value, id: value });
        } else if (name === "id") {
            this.setState({ id: value });
        }
    };
 
    checkErrors = (): Promise<boolean> => {
        const state = this.state;
        return new Promise<boolean>((resolve) => {
            if (!state.title) {
                AppUtils.showErrorMessage(Translations.TitleMustBeDefined);
                return resolve(false);
            }
            if (this.props.usedOptionTitles.find(i => i.equalIgnoreCase(state.title))) {
                AppUtils.showErrorMessage(Translations.TitleIsAlreadyInUse);
                return resolve(false);
            }
            return resolve(true);
        });
    };

    saveToDb = async (): Promise<boolean> => {
        const state = this.state;
        if (!await AppUtils.validate(this.checkErrors)) return false;
        this.orgStateHash = this.getSaveDataFromState(this.state).hash;
        this.props.onSaved(IdTitle.createIdTitle(state.id, state.title));
        return true;
    };

    handleSave = async (closeDialog: boolean) => {
        const result = await this.saveToDb();
        if (!result) return;
        if (!closeDialog) {
            return;
        }
        this.props.onClose();
    };

    handleRemove = async () => {
        const props = this.props;
        if (props.isNew) return;
        props.onRemove(props.idOption.title);
    };

    handleCancel = async () => {
        const props = this.props;
        const state = this.state;
        const cancelResult = await AppUtils.cancel(this.getSaveDataFromState(state).hash, this.orgStateHash, null);
        if (cancelResult === ConfirmationDialogResult.Cancel) return;
        if (cancelResult === ConfirmationDialogResult.Yes) {
            this.handleSave(true);
        } else {
            props.onClose();
        }
    };

    canMoveOut = async (): Promise<boolean> => {
        return await AppUtils.canMoveOut(this.getSaveDataFromState(this.state).hash, this.orgStateHash,
            this.saveToDb,
            null,
            (newStateHash) => { this.orgStateHash = newStateHash; }
        );
    };

    handlePrevious = async () => {
        if (!await this.canMoveOut()) return;
        this.props.onPrevious();
    };

    handleNext = async () => {
        if (!await this.canMoveOut()) return;
        this.props.onNext();
    };

    render() {
        const props = this.props;
        const state = this.state;
        if (!props.idOption) return null;
        const isNew = props.isNew;
        return (
            <Dialog
                id="taskFormDesignerOptionDialog"
                maxWidth="sm"
                title={isNew
                    ? Translations.FormAddOption
                    : (Translations.FormOption + ": " + props.idOption.title)}
                body={
                    <Box display="flex" flexDirection="column">
                        <GridRow>
                            <Grid item sm={12}>
                                <FormField title={Translations.Title} fullWidth required margin="0">
                                    <TextField name="title" title={Translations.Title} inputProps={{ maxLength: 100 }} value={state.title} required={true} onChange={this.handleChange} size="small" fullWidth />
                                </FormField>
                            </Grid>
                        </GridRow>
                        <GridRow>
                            <Grid item sm={12}>
                                <FormField title={Translations.Value} fullWidth>
                                    <TextField name="id" title={Translations.Value} inputProps={{ maxLength: 100 }} value={state.id} required={true} onChange={this.handleChange} size="small" fullWidth />
                                </FormField>
                            </Grid>
                        </GridRow>
                    </Box>
                }
                actions={
                    <Box display="flex" justifyContent="space-between" width="100%">
                        <DialogItemsNav
                            hasPrevious={!isNew && props.hasPrevious}
                            hasNext={!isNew && props.hasNext}
                            onPrevious={this.handlePrevious}
                            onNext={this.handleNext}
                        />
                        <DialogButtons
                            isNew={isNew}
                            saveTitle={Translations.OK}
                            onCancel={this.handleCancel}
                            onRemove={!isNew ? this.handleRemove : null}
                            onSave={() => this.handleSave(false)}
                        />
                    </Box>
                }
                onClose={this.handleCancel}
            />
        );
    }
}